import recipe from '../../../recipes/audio/soundscapes.json' with { type: 'json' };

export type SoundPoint = readonly [number, number];
export type Surface = 'stone' | 'brick' | 'gravel' | 'timber' | 'water-edge';
export interface SoundSource {
  cue: string; caption: string; gain: number; at?: SoundPoint; radius?: number;
  day?: readonly [number, number, number];
}
export interface Soundscape {
  surface: Surface; theme: string; layers: readonly SoundSource[];
  surfaces?: readonly { surface: Surface; polygon: readonly SoundPoint[] }[];
}
const places = recipe.places as unknown as Readonly<Record<string, Soundscape>>;
export const soundscape = (place: string, beat: string): Soundscape | undefined => places[beat] ?? places[place];

/** Acoustic time belongs to presentation only. The host advances it while the scene runs. */
export function acousticDay(seconds: number): { hour: number; phase: string; weights: readonly [number, number, number] } {
  const hour = (17 + seconds / 90) % 24;
  const daylight = Math.max(0, Math.min(1, (Math.cos((hour - 12) * Math.PI / 12) + .15) * 2));
  const night = Math.max(0, Math.min(1, (-Math.cos((hour - 12) * Math.PI / 12) - .15) * 2));
  return { hour, phase: hour < 5 || hour >= 20 ? 'Night' : hour < 8 ? 'Dawn' : hour < 16 ? 'Day' : 'Dusk', weights: [daylight, 1 - daylight - night, night] };
}

/** Soft distance rolloff and a modest stereo image keep mono speakers equally informative. */
export function sourceMix(source: SoundSource, at: SoundPoint, weights: readonly number[]): { gain: number; pan: number; cutoff: number } {
  const distance = source.at ? Math.hypot(source.at[0] - at[0], (source.at[1] - at[1]) * 1.4) / (source.radius ?? 360) : 0;
  const near = 1 / (1 + distance * distance * 3);
  const time = source.day ? source.day.reduce((sum, gain, i) => sum + gain * weights[i]!, 0) : 1;
  return { gain: source.gain * near * time, pan: source.at ? Math.max(-.75, Math.min(.75, (source.at[0] - at[0]) / 650)) : 0, cutoff: 1800 + 13200 * near };
}

export function surfaceAt(place: Soundscape | undefined, [x, y]: SoundPoint): Surface {
  for (const patch of place?.surfaces ?? []) {
    let inside = false;
    for (let i = 0, j = patch.polygon.length - 1; i < patch.polygon.length; j = i++) {
      const [ax, ay] = patch.polygon[i]!, [bx, by] = patch.polygon[j]!;
      if ((ay > y) !== (by > y) && x < (bx - ax) * (y - ay) / (by - ay) + ax) inside = !inside;
    }
    if (inside) return patch.surface;
  }
  return place?.surface ?? 'stone';
}
